import {inject, Inject, Injectable, signal} from "@angular/core";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {ResellerTuningCombinationsModel} from "../models/Reseller-tuning-combinations-model";
import {NotificationService} from "./Notification.service";
import {DeleteDialogComponent} from "../helpers/delete-dialog/delete-dialog.component";
import {MatDialog} from "@angular/material/dialog";


@Injectable({
    providedIn: 'root'
})
export class ResellerTuningCombinationsService {

    private url = `${this.API_URL}`;

    resellerTuningCombinations = signal<ResellerTuningCombinationsModel[]>([])
    isLoading = signal<boolean>(false)
    showNewInputRow = signal<boolean>(false);
    isWaitingForResponse = signal<boolean>(false);
    resellerTuningIndex = signal<number>(-1);
    resellerTuningList = signal<string[]>([]);
    resellerProjectTypeList = signal<string[]>([]);
    public newResellerRow = signal<ResellerTuningCombinationsModel>({} as ResellerTuningCombinationsModel);


    notificationService = inject(NotificationService);
    dialog = inject(MatDialog);


    constructor(@Inject('API_URL') private API_URL: string, private httpClient: HttpClient) {
    }


    resetTableValues() {
        this.resellerTuningCombinations.set([]);
    }

    getAllResellerCombinations() {
        this.isLoading.set(true);
        this.httpClient.get<ResellerTuningCombinationsModel[]>(`${this.url}/resellerTuningCombinations`).subscribe(response => {
            const tuningOptionsList: string[] = [];
            const projectTypeList: string[] = [];

            response.forEach(reseller => {
                if (reseller.tuningOptions && !tuningOptionsList.includes(reseller.tuningOptions)) {
                    tuningOptionsList.push(reseller.tuningOptions);
                }
                if (reseller.resellerProjectType && !projectTypeList.includes(reseller.resellerProjectType)) {
                    projectTypeList.push(reseller.resellerProjectType);
                }
            });

            tuningOptionsList.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
            projectTypeList.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

            this.resellerTuningList.set(tuningOptionsList);
            this.resellerProjectTypeList.set(projectTypeList);
            this.resellerTuningCombinations.set(response);
            this.isLoading.set(false);
        });
    }


    validateResellerOptions(
        tuningOptions: string | null | undefined,
        resellerProjectType: string | null | undefined,
        currentId?: number
    ): { tuningOptions?: string; resellerProjectType?: string } {
        const options = tuningOptions?.trim() ?? '';
        const projectType = resellerProjectType?.trim() ?? '';
        const errors: { tuningOptions?: string; resellerProjectType?: string } = {};

        if (!options) {
            errors.tuningOptions = 'Tuning options are required.';
        } else if (this.resellerTuningCombinations().some(row =>
            row.id !== currentId && row.tuningOptions?.trim().toLowerCase() === options.toLowerCase()
        )) {
            errors.tuningOptions = 'Tuning option already exists.';
        }

        if (!projectType) {
            errors.resellerProjectType = 'Project type is required.';
        }

        return errors;
    }

    addNewResellerOptions(newResellerOptionRow: ResellerTuningCombinationsModel) {
        if (this.isWaitingForResponse() || Object.keys(this.validateResellerOptions(
            newResellerOptionRow.tuningOptions, newResellerOptionRow.resellerProjectType
        )).length > 0) {
            return;
        }

        this.isWaitingForResponse.set(true);
        this.httpClient.post<ResellerTuningCombinationsModel>(`${this.url}/resellerTuningCombinations`, {
            id: newResellerOptionRow.id,
            newTuningCombinationOptions: newResellerOptionRow.tuningOptions.trim(),
            newResellerProjectType: newResellerOptionRow.resellerProjectType.trim()
        }, {observe: 'response'}).subscribe({
            next: response => {
                this.isWaitingForResponse.set(false);
                if (response.status === 201 && response.body) {
                    this.resellerTuningCombinations.update(rows => [...rows, response.body!]);
                    this.refreshLists();
                    this.showNewInputRow.set(false);
                    this.resetNewResellerRowValue();
                    this.notificationService.showSuccessMessage(
                        'Successfully added.', 'Successfully added new Reseller Tuning Combination.'
                    );
                }
            },
            error: (error: HttpErrorResponse) => {
                this.isWaitingForResponse.set(false);
                this.handleSaveError(error);
            }
        });
    }

    editResellerOptions(id: number, resellerOptions: ResellerTuningCombinationsModel) {
        if (this.isWaitingForResponse() || Object.keys(this.validateResellerOptions(
            resellerOptions.tuningOptions, resellerOptions.resellerProjectType, id
        )).length > 0) {
            return;
        }

        this.isWaitingForResponse.set(true);
        const requestBody = {
            newTuningCombinationOptions: resellerOptions.tuningOptions.trim(),
            newResellerProjectType: resellerOptions.resellerProjectType.trim()
        };

        this.httpClient.put<ResellerTuningCombinationsModel>(
            `${this.url}/resellerTuningCombinations/${id}`, requestBody, {observe: 'response'}
        ).subscribe({
            next: response => {
                this.isWaitingForResponse.set(false);
                if (response.status === 200 && response.body) {
                    this.resellerTuningCombinations.update(rows =>
                        rows.map(row => row.id === id ? response.body! : row)
                    );
                    this.refreshLists();
                    this.closeEditedItem();
                    this.notificationService.showSuccessMessage(
                        'Reseller Tuning Combination successfully updated', 'Reseller Tuning updated'
                    );
                }
            },
            error: (error: HttpErrorResponse) => {
                this.isWaitingForResponse.set(false);
                this.handleSaveError(error);
            }
        });
    }

    private handleSaveError(error: HttpErrorResponse) {
        if (error.status === 409) {
            this.notificationService.showErrorMessage('Tuning option already exists.', 'Conflict');
            return;
        }

        const fieldErrors = error.error?.errors;
        if (fieldErrors && typeof fieldErrors === 'object') {
            for (const [field, message] of Object.entries(fieldErrors)) {
                this.notificationService.showErrorMessage(String(message), field);
            }
            return;
        }

        this.notificationService.showErrorMessage('Unable to save reseller tuning combination.', 'Error');
    }


    deleteItemFromDatabase(id: number) {
        const dialogRef = this.dialog.open(DeleteDialogComponent, {
            width: "350px",
            data: "Are you sure you want to delete Reseller Option?"
        })
        dialogRef.afterClosed().subscribe(confirmed => {
                if (confirmed) {
                    this.httpClient.delete(`${this.url}/resellerTuningCombinations/${id}`, {
                        params: {resellerId: id},
                        observe: "response"
                    }).subscribe((response) => {
                        if (response.status === 200) {
                            this.resellerTuningCombinations.update((resellers) => resellers.filter(reseller => reseller.id !== id));
                            this.notificationService.showSuccessMessage('Successfully deleted', `Reseller Tuning Combination  ${id} deleted.`);
                            this.refreshLists()
                        } else {
                            this.notificationService.showErrorMessage('Something went wrong', 'Error',);
                        }

                    })
                }
            }
        )
    }

    refreshLists() {
        const tuningOptionsList = Array.from(new Set(this.resellerTuningCombinations().map(item => item.tuningOptions).filter(Boolean)));
        const projectTypeList = Array.from(new Set(this.resellerTuningCombinations().map(item => item.resellerProjectType).filter(Boolean)));

        this.resellerTuningList.set(tuningOptionsList.sort());
        this.resellerProjectTypeList.set(projectTypeList.sort());
    }


    resetNewResellerRowValue() {
        this.newResellerRow.set({} as ResellerTuningCombinationsModel);
    }

    toggleNewInputRow() {
        this.showNewInputRow.set(true);
    }

    toggleEditView(index: number) {
        this.resellerTuningIndex.set(index);
    }


    closeRow() {
        this.showNewInputRow.set(false);
    }

    closeEditedItem() {
        this.resellerTuningIndex.set(-1);
    }

}